/**
 * Linkify well-known Chilean tax norms mentioned in assistant responses so
 * users can jump straight to the official LeyChile (BCN) text.
 *
 * Only three norms have a verified idNorma deep link (LIR, IVA, Código
 * Tributario). Everything else ("Ley N° 21.210", etc.) falls back to a BCN
 * search URL — we never invent idNorma values for laws we haven't verified.
 */

const LIR_URL = 'https://www.bcn.cl/leychile/navegar?idNorma=6368';
const IVA_URL = 'https://www.bcn.cl/leychile/navegar?idNorma=6369';
const CODIGO_TRIBUTARIO_URL = 'https://www.bcn.cl/leychile/navegar?idNorma=6374';

/** Verified LeyChile deep links for the big three tax norms. */
export const NORM_LINKS: Record<string, string> = {
  LIR: LIR_URL,
  IVA: IVA_URL,
  CODIGO_TRIBUTARIO: CODIGO_TRIBUTARIO_URL,
};

/** Generic BCN search fallback for "Ley N° dd.ddd"-style references. */
export function genericLeyUrl(numero: string): string {
  const clean = numero.replace(/\./g, '');
  return `https://www.bcn.cl/leychile/consulta/listaresultadosimple?cadena=${encodeURIComponent('Ley ' + clean)}`;
}

interface NormVariant {
  regex: RegExp;
  url: string;
}

interface NormGroup {
  key: string;
  patterns: NormVariant[];
}

const NORM_GROUPS: NormGroup[] = [
  {
    key: 'LIR',
    patterns: [
      { regex: /\bLIR\b/gi, url: LIR_URL },
      { regex: /Ley sobre Impuesto a la Renta/gi, url: LIR_URL },
      { regex: /Ley de Impuesto a la Renta/gi, url: LIR_URL },
      { regex: /\bD\.?L\.?\s*N?[°º]?\.?\s*824\b/gi, url: LIR_URL },
      { regex: /\bDecreto\s+Ley\s*N?[°º]?\.?\s*824\b/gi, url: LIR_URL },
    ],
  },
  {
    key: 'IVA',
    patterns: [
      { regex: /Ley de IVA/gi, url: IVA_URL },
      { regex: /Ley del IVA/gi, url: IVA_URL },
      { regex: /\bD\.?L\.?\s*N?[°º]?\.?\s*825\b/gi, url: IVA_URL },
      { regex: /\bDecreto\s+Ley\s*N?[°º]?\.?\s*825\b/gi, url: IVA_URL },
    ],
  },
  {
    key: 'CODIGO_TRIBUTARIO',
    patterns: [
      { regex: /C[oó]digo Tributario/gi, url: CODIGO_TRIBUTARIO_URL },
      { regex: /\bD\.?L\.?\s*N?[°º]?\.?\s*830\b/gi, url: CODIGO_TRIBUTARIO_URL },
      { regex: /\bDecreto\s+Ley\s*N?[°º]?\.?\s*830\b/gi, url: CODIGO_TRIBUTARIO_URL },
    ],
  },
];

// Generic "Ley N° 21.210" style fallback (only runs where nothing above matched).
const GENERIC_LEY_REGEX = /\bLey\s+(?:N[°º]?\.?\s*)?(\d{1,2}\.?\d{3})\b/gi;

// Splits on already-existing markdown links `[text](url)` so we never touch
// text that's already linked — this also protects `[CITE](#cite-uuid)` tokens,
// since those are rewritten into markdown links before linkifyNorms runs.
const EXISTING_LINK_REGEX = /(\[[^\]]*\]\([^)]*\))/g;

function findEarliestMatch(text: string, group: NormGroup): { index: number; text: string; url: string } | null {
  let best: { index: number; text: string; url: string } | null = null;
  for (const variant of group.patterns) {
    variant.regex.lastIndex = 0;
    const m = variant.regex.exec(text);
    if (m && (best === null || m.index < best.index)) {
      best = { index: m.index, text: m[0], url: variant.url };
    }
  }
  return best;
}

function linkifyGenericLey(segment: string, matchedNumbers: Set<string>): string {
  GENERIC_LEY_REGEX.lastIndex = 0;
  let result = '';
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = GENERIC_LEY_REGEX.exec(segment)) !== null) {
    const numero = match[1];
    const normalized = numero.replace(/\./g, '');
    if (matchedNumbers.has(normalized)) {
      // Already linked once earlier in the message — leave this repeat as-is.
      continue;
    }
    const matchedText = match[0];
    result += segment.slice(cursor, match.index) + `[${matchedText}](${genericLeyUrl(numero)})`;
    cursor = match.index + matchedText.length;
    matchedNumbers.add(normalized);
  }
  result += segment.slice(cursor);
  return result;
}

function linkifySegment(segment: string, matchedKeys: Set<string>, matchedNumbers: Set<string>): string {
  let result = segment;

  for (const group of NORM_GROUPS) {
    if (matchedKeys.has(group.key)) continue;
    const found = findEarliestMatch(result, group);
    if (found) {
      result =
        result.slice(0, found.index) +
        `[${found.text}](${found.url})` +
        result.slice(found.index + found.text.length);
      matchedKeys.add(group.key);
    }
  }

  return linkifyGenericLey(result, matchedNumbers);
}

/**
 * Replace mentions of well-known Chilean tax norms with markdown links.
 * Safe to run on already-processed markdown (e.g. content that already has
 * `[CITE](#cite-uuid)` links) — existing links are left untouched, and only
 * the first occurrence of each distinct norm is linkified to avoid link spam.
 */
export function linkifyNorms(markdown: string): string {
  if (!markdown) return markdown;

  const segments = markdown.split(EXISTING_LINK_REGEX);
  const matchedKeys = new Set<string>();
  const matchedNumbers = new Set<string>();

  return segments
    .map((segment, idx) => {
      // Odd indices are the captured existing links (incl. #cite- links) — untouched.
      if (idx % 2 === 1) return segment;
      return linkifySegment(segment, matchedKeys, matchedNumbers);
    })
    .join('');
}
